/**
 * Sticky last-known pill-variant cache (vision-only trial).
 *
 * With Tesseract retired and pill OCR routed to Apple Vision on a raw crop, a
 * marginal frame (mid-scroll blur, PiP overlap, transient glare) can return an
 * EMPTY pill read even though the active variant has not changed. This cache
 * holds the last confidently-read variant and re-applies it on an empty frame,
 * so a single dropped read does not stall the scan — while INVALIDATING the
 * cached variant the moment the evidence says the page actually changed:
 *   - the visible region changed (user scrolled to a different dex region), or
 *   - the frame's dominant body hue swung more than a threshold (the page tint,
 *     hence the variant family, changed), or
 *   - two consecutive frames confidently read a DIFFERENT variant (a real tab
 *     switch, debounced so one misread cannot overwrite the cache).
 *
 * Colour is corroboration-only here too: the hue swing INVALIDATES a stale
 * cache, it never positively selects a variant. Pure/total and DOM-free so it
 * is trivially unit-testable on synthetic observations.
 */

import type { PokedexVariant } from "./pogoPokedexVariantClassifier";
// PokedexRegion is declared in pokedexOcr.ts (there is no pokedexRegions.ts).
import type { PokedexRegion } from "./pokedexOcr";

export interface StickyPillEntry {
  variant: PokedexVariant;
  region: PokedexRegion;
  frameIdx: number;
  hueAtConfirm: number | null;
}

export interface StickyPillObservation {
  variant: PokedexVariant | null;
  confidence: number;
  region: PokedexRegion;
  /** Dominant body hue this frame; nullable if not computed. */
  frameHue: number | null;
  frameIdx: number;
}

export interface StickyPillDecision {
  variant: PokedexVariant | null;
  source: "fresh" | "sticky" | "none";
  invalidated: null | "region_change" | "hue_swing" | "consecutive_different";
}

export interface StickyPillCache {
  observe(obs: StickyPillObservation): StickyPillDecision;
  current(): StickyPillEntry | null;
  reset(): void;
}

/**
 * Circular distance between two hue angles in degrees, 0..180.
 * `angularDistance(a, b) = min(|a - b|, 360 - |a - b|)`.
 */
export function angularDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return Math.min(d, 360 - d);
}

export function createStickyPillCache(opts?: {
  hueInvalidationThresholdDeg?: number; // default 25
  consecutiveDifferentThreshold?: number; // default 2 (2-of-3 debounce)
  minConfidenceForConfirm?: number; // default 55
}): StickyPillCache {
  const hueThreshold = opts?.hueInvalidationThresholdDeg ?? 25;
  const consecutiveThreshold = opts?.consecutiveDifferentThreshold ?? 2;
  const minConf = opts?.minConfidenceForConfirm ?? 55;

  let entry: StickyPillEntry | null = null;
  // Count of consecutive confident reads whose variant DIFFERS from the cache.
  let consecutiveDifferentCount = 0;

  function setFresh(obs: StickyPillObservation): void {
    entry = {
      variant: obs.variant as PokedexVariant,
      region: obs.region,
      frameIdx: obs.frameIdx,
      hueAtConfirm: obs.frameHue,
    };
    consecutiveDifferentCount = 0;
  }

  return {
    observe(obs: StickyPillObservation): StickyPillDecision {
      const hasVariant = obs.variant != null && obs.confidence >= minConf;

      // ── Confident (fresh) read ──────────────────────────────────────────
      if (hasVariant) {
        if (entry == null) {
          // First successful read populates the cache.
          setFresh(obs);
          return { variant: obs.variant, source: "fresh", invalidated: null };
        }
        if (obs.variant === entry.variant) {
          // A matching confident read refreshes the cache and resets the
          // consecutive-different debounce counter.
          setFresh(obs);
          return { variant: obs.variant, source: "fresh", invalidated: null };
        }
        // A DIFFERENT confident variant. Debounce: a single misread must not
        // overwrite the cache. Increment the counter; only at the threshold do
        // we accept the new variant as a fresh entry.
        consecutiveDifferentCount += 1;
        if (consecutiveDifferentCount >= consecutiveThreshold) {
          setFresh(obs); // resets consecutiveDifferentCount to 0
          return {
            variant: obs.variant,
            source: "fresh",
            invalidated: "consecutive_different",
          };
        }
        // Below threshold: keep the cached variant (single-misread protection).
        return { variant: entry.variant, source: "sticky", invalidated: null };
      }

      // ── Empty read (no confident variant this frame) ────────────────────
      if (entry == null) {
        return { variant: null, source: "none", invalidated: null };
      }

      // Guard 1: region change invalidates the cache.
      if (obs.region !== entry.region) {
        entry = null;
        consecutiveDifferentCount = 0;
        return { variant: null, source: "none", invalidated: "region_change" };
      }

      // Guard 2: a large body-hue swing (both hues present) invalidates.
      if (
        obs.frameHue != null &&
        entry.hueAtConfirm != null &&
        angularDistance(obs.frameHue, entry.hueAtConfirm) > hueThreshold
      ) {
        entry = null;
        consecutiveDifferentCount = 0;
        return { variant: null, source: "none", invalidated: "hue_swing" };
      }

      // Otherwise: apply the cached variant.
      return { variant: entry.variant, source: "sticky", invalidated: null };
    },

    current(): StickyPillEntry | null {
      return entry;
    },

    reset(): void {
      entry = null;
      consecutiveDifferentCount = 0;
    },
  };
}
